/**
 * Per-day slot loading for the B2C reschedule grid (PLA-878).
 *
 * One existing `getOptimalBookingTimesActive` call per visible day, in
 * parallel, cached per date for the modal's lifetime. A generation counter
 * (same idea as PLA-715's fetchRequestIdRef) drops responses that belong to a
 * superseded modal session so they can never land on the current one.
 * Logging follows ADR-0036: a single failed day is a breadcrumb, a page left
 * with no times at all is a captured exception (the Sentry beforeSend hook
 * downgradeExpectedThrow tiers expected callable rejections to warning).
 */
import { useCallback, useEffect, useRef, useState } from "react"
import * as Sentry from "@sentry/react"
import dayjs from "dayjs"

import { getOptimalBookingTimesActive } from "@/services/firebase/functions"

import {
  deriveDaySlots,
  slotQueryTime,
  type DayResult,
  type OpeningHoursData,
  type ServiceType,
} from "./rescheduleSlotModel"

/** Upper bound per day, as in the pre-grid flow (PLA-715). */
export const DAY_TIMEOUT_MS = 20000

interface Params {
  active: boolean
  location: string
  serviceType: ServiceType
  hours: OpeningHoursData | null
  dates: string[]
}

export function useRescheduleSlots({ active, location, serviceType, hours, dates }: Params): {
  results: Record<string, DayResult>
  refetchDay: (date: string) => void
} {
  const [results, setResults] = useState<Record<string, DayResult>>({})
  const cacheRef = useRef<Record<string, DayResult>>({})
  const generationRef = useRef(0)
  // Per-day request generation: only the latest request for a date may write its outcome.
  const requestTokenRef = useRef<Record<string, number>>({})
  // Bumped by refetchDay so the fetch effect re-runs for a day dropped from the cache.
  const [refreshTick, setRefreshTick] = useState(0)

  // New modal session / different service or location: drop everything.
  useEffect(() => {
    generationRef.current += 1
    cacheRef.current = {}
    setResults({})
  }, [active, location, serviceType])

  const datesKey = dates.join(",")

  /** Forget a day and fetch it again (slot-taken recovery, per-day retry). */
  const refetchDay = useCallback((date: string) => {
    // Supersede any request still in flight for this day, before the retry even starts.
    requestTokenRef.current[date] = (requestTokenRef.current[date] ?? 0) + 1
    delete cacheRef.current[date]
    setRefreshTick((n) => n + 1)
  }, [])

  useEffect(() => {
    if (!active || !hours || dates.length === 0) return
    const generation = generationRef.current
    const isStale = () => generationRef.current !== generation
    // Cached days are never refetched; failed days retry on the next visit.
    const toFetch = dates.filter((d) => {
      const cur = cacheRef.current[d]
      return cur === undefined || cur === "error"
    })
    if (toFetch.length === 0) return

    toFetch.forEach((d) => {
      cacheRef.current[d] = "loading"
    })
    setResults({ ...cacheRef.current })

    const now = dayjs()
    let failed = 0
    let settled = 0
    let lastError: unknown
    const publish = () => setResults({ ...cacheRef.current })

    // Page-level ADR-0036 check once every day of this batch has a first outcome.
    const onDaySettled = () => {
      settled += 1
      if (settled < toFetch.length || isStale()) return
      const pageHasTimes = dates.some((d) => {
        const r = cacheRef.current[d]
        return typeof r === "object" && r.times.length > 0
      })
      if (failed > 0 && !pageHasTimes) {
        Sentry.captureException(lastError, {
          tags: { flow: "reschedule", operation: "fetch-optimal-times" },
          extra: { location, failedDays: failed },
        })
      }
    }

    // Each day publishes on its own, so one stalled call never blocks the rest.
    const run = (date: string) => {
      let done = false
      const token = (requestTokenRef.current[date] = (requestTokenRef.current[date] ?? 0) + 1)
      const isSuperseded = () => isStale() || requestTokenRef.current[date] !== token
      const fail = (error: unknown) => {
        if (done || isSuperseded()) return
        done = true
        failed += 1
        lastError = error
        cacheRef.current[date] = "error"
        Sentry.addBreadcrumb({
          category: "reschedule",
          level: "warning",
          message: "fetch-optimal-times failed for day",
          data: { date, location },
        })
        publish()
        onDaySettled()
      }
      const timer = setTimeout(
        () => fail(new Error("fetch-optimal-times timed out")),
        DAY_TIMEOUT_MS,
      )
      getOptimalBookingTimesActive(location, date, slotQueryTime(date, hours, now), serviceType)
        .then((resp) => {
          clearTimeout(timer)
          if (isSuperseded()) return
          // A response arriving after the timeout still fills the cache: results
          // are keyed by date, so it can only improve that day's column and never
          // touches another page's state. It replaces the timeout error.
          cacheRef.current[date] = deriveDaySlots(resp)
          publish()
          if (!done) {
            done = true
            onDaySettled()
          }
        })
        .catch((error) => {
          clearTimeout(timer)
          fail(error)
        })
    }
    toFetch.forEach(run)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, hours, datesKey, location, serviceType, refreshTick])

  return { results, refetchDay }
}
